import React, { useState } from 'react';
import { Calendar, Users, Gift, Star, TrendingUp } from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';

interface Event {
  id: string;
  date: string;
  name: string;
  type: 'holiday' | 'local' | 'campaign' | 'competitor';
  impact: 'high' | 'medium' | 'low';
  expectedIncrease: number;
  affectedCategories: string[];
  description: string;
}

const EventCalendar: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState(9); // 9月
  
  const events: Event[] = [
    {
      id: 'evt1',
      date: '2025-09-23',
      name: '秋分の日',
      type: 'holiday',
      impact: 'high',
      expectedIncrease: 25,
      affectedCategories: ['レジャー用品', 'お弁当', '飲料'],
      description: '祝日による来店客数増加が見込まれます'
    },
    {
      id: 'evt2',
      date: '2025-09-27',
      name: '地域秋祭り',
      type: 'local',
      impact: 'medium',
      expectedIncrease: 15,
      affectedCategories: ['お祭り用品', '軽食', '飲料'],
      description: '地域イベントによる特需'
    },
    {
      id: 'evt3',
      date: '2025-10-01',
      name: '創業記念セール',
      type: 'campaign',
      impact: 'high',
      expectedIncrease: 40,
      affectedCategories: ['全カテゴリ'],
      description: '大規模セールによる売上増'
    },
    {
      id: 'evt4',
      date: '2025-10-08',
      name: '競合店オープン',
      type: 'competitor',
      impact: 'high',
      expectedIncrease: -20,
      affectedCategories: ['全カテゴリ'],
      description: '近隣に競合店がオープン、客足への影響注意'
    },
    {
      id: 'evt5',
      date: '2025-10-31',
      name: 'ハロウィン',
      type: 'holiday',
      impact: 'high',
      expectedIncrease: 35,
      affectedCategories: ['お菓子', 'パーティー用品', '仮装グッズ'],
      description: 'ハロウィン関連商品の需要急増'
    }
  ];

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'holiday': return <Calendar size={16} style={{ color: colors.accent.red }} />;
      case 'local': return <Users size={16} style={{ color: colors.accent.blue }} />;
      case 'campaign': return <Gift size={16} style={{ color: colors.support.success }} />;
      case 'competitor': return <Star size={16} style={{ color: colors.status.warning }} />;
      default: return <Calendar size={16} />;
    }
  };

  const getEventTypeName = (type: string) => {
    switch (type) {
      case 'holiday': return '祝日・記念日';
      case 'local': return '地域イベント';
      case 'campaign': return 'キャンペーン';
      case 'competitor': return '競合動向';
      default: return 'その他';
    }
  };

  const getImpactBadge = (impact: string) => {
    switch (impact) {
      case 'high': return <Badge variant="error">高影響</Badge>;
      case 'medium': return <Badge variant="warning">中影響</Badge>;
      case 'low': return <Badge variant="secondary">低影響</Badge>;
      default: return <Badge variant="secondary">-</Badge>;
    }
  };

  // カレンダーグリッドの生成
  const generateCalendarGrid = () => {
    const year = 2025;
    const firstDay = new Date(year, selectedMonth - 1, 1).getDay();
    const daysInMonth = new Date(year, selectedMonth, 0).getDate();
    const grid = [];
    
    // 空白セル
    for (let i = 0; i < firstDay; i++) {
      grid.push(null);
    }
    
    // 日付セル
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `2025-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayEvents = events.filter(e => e.date === dateStr);
      grid.push({ day, events: dayEvents });
    }
    
    return grid;
  };

  const calendarGrid = generateCalendarGrid();

  return (
    <div className="space-y-6">
      {/* 月選択 */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold" style={{ color: colors.text.primary }}>
          イベントカレンダー
        </h3>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="small"
            onClick={() => setSelectedMonth(Math.max(1, selectedMonth - 1))}
          >
            前月
          </Button>
          <span className="px-4 py-2 font-medium">
            2025年{selectedMonth}月
          </span>
          <Button
            variant="secondary"
            size="small"
            onClick={() => setSelectedMonth(Math.min(12, selectedMonth + 1))}
          >
            翌月
          </Button>
        </div>
      </div>

      {/* カレンダー表示 */}
      <Card className="p-4">
        <div className="grid grid-cols-7 gap-1">
          {/* 曜日ヘッダー */}
          {['日', '月', '火', '水', '木', '金', '土'].map(day => (
            <div 
              key={day} 
              className="text-center text-xs font-medium p-2"
              style={{ 
                color: colors.text.secondary,
                backgroundColor: colors.ui.bgSecondary 
              }}
            >
              {day}
            </div>
          ))}
          
          {/* 日付グリッド */}
          {calendarGrid.map((cell, index) => (
            <div
              key={index}
              className="border h-20 p-1 relative"
              style={{ borderColor: colors.ui.border }}
            >
              {cell && (
                <>
                  <div className="text-xs font-medium mb-1">
                    {cell.day}
                  </div>
                  {cell.events.map(event => (
                    <div
                      key={event.id}
                      className="text-xs p-1 rounded cursor-pointer hover:opacity-80"
                      style={{ 
                        backgroundColor: event.type === 'competitor' ? colors.status.warning + '20' :
                                      event.type === 'holiday' ? colors.accent.red + '20' :
                                      event.type === 'campaign' ? colors.support.success + '20' :
                                      colors.accent.blue + '20'
                      }}
                      title={event.description}
                    >
                      <div className="flex items-center gap-1">
                        {getEventIcon(event.type)}
                        <span className="truncate">{event.name}</span>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* イベント詳細リスト */}
      <Card className="p-4">
        <h4 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
          今後のイベントと影響予測
        </h4>
        
        <div className="space-y-3">
          {events.map(event => (
            <div 
              key={event.id}
              className="flex items-start gap-4 p-3 rounded-lg border"
              style={{ 
                borderColor: colors.ui.border,
                backgroundColor: event.expectedIncrease < 0 ? colors.status.warning + '10' : 'transparent'
              }}
            >
              <div className="mt-1">{getEventIcon(event.type)}</div>
              
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium" style={{ color: colors.text.primary }}>
                    {event.name}
                  </span>
                  {getImpactBadge(event.impact)}
                  <Badge variant="secondary">{getEventTypeName(event.type)}</Badge>
                </div>
                
                <div className="text-sm" style={{ color: colors.text.secondary }}>
                  {new Date(event.date).toLocaleDateString('ja-JP', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    weekday: 'long'
                  })}
                </div>
                
                <div className="mt-2">
                  <span className="text-sm">{event.description}</span>
                </div>
                
                <div className="mt-2 flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <TrendingUp size={14} style={{ 
                      color: event.expectedIncrease > 0 ? colors.support.success : colors.status.error 
                    }} />
                    <span className="text-sm font-medium" style={{ 
                      color: event.expectedIncrease > 0 ? colors.support.success : colors.status.error 
                    }}>
                      売上予測: {event.expectedIncrease > 0 ? '+' : ''}{event.expectedIncrease}%
                    </span>
                  </div>
                  
                  <div className="flex gap-2">
                    {event.affectedCategories.map((cat, idx) => (
                      <Badge key={idx} variant="secondary" size="small">
                        {cat}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              
              <Button variant="secondary" size="small">
                詳細分析
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {/* 統計サマリー */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="text-sm" style={{ color: colors.text.secondary }}>
            今月のイベント数
          </div>
          <div className="text-2xl font-bold mt-1" style={{ color: colors.text.primary }}>
            {events.filter(e => e.date.startsWith(`2025-${String(selectedMonth).padStart(2, '0')}`)).length}件
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="text-sm" style={{ color: colors.text.secondary }}>
            平均売上影響
          </div>
          <div className="text-2xl font-bold mt-1" style={{ color: colors.support.success }}>
            +{Math.round(events.reduce((sum, e) => sum + e.expectedIncrease, 0) / events.length)}%
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="text-sm" style={{ color: colors.text.secondary }}>
            高影響イベント
          </div>
          <div className="text-2xl font-bold mt-1" style={{ color: colors.accent.red }}>
            {events.filter(e => e.impact === 'high').length}件
          </div>
        </Card>
      </div>
    </div>
  );
};

export default EventCalendar;
